import { Toast } from '@base-ui/react/toast';
import { CheckCircleIcon, InfoIcon, WarningIcon, XIcon, XCircleIcon } from '@phosphor-icons/react';
import type { ComponentType, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { toastManager, type ToastVariant } from './toast';

const variantClassName: Record<ToastVariant, string> = {
  success: 'bg-green-300 text-green-700',
  error: 'bg-misc-errorlight text-misc-error',
  info: 'bg-bluegray-200 text-bluegray-600',
  warning: 'bg-accent-orangelight text-accent-orange'
};

const stackClassName = [
  '[--gap:0.75rem] [--peek:0.75rem]',
  '[--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]',
  '[--height:var(--toast-frontmost-height,var(--toast-height))]',
  '[--offset-y:calc(var(--toast-offset-y)+(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))]',
  'absolute top-0 right-0 left-auto z-[calc(1000-var(--toast-index))] w-full origin-top',
  '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))]',
  'h-[var(--height)] data-[expanded]:h-[var(--toast-height)]',
  'data-[expanded]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--offset-y)))]',
  'data-[limited]:opacity-0 data-[ending-style]:opacity-0',
  'data-[starting-style]:[transform:translateY(-150%)]',
  '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]',
  'data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
  'data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
  'data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
  'data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
  'before:absolute before:top-full before:left-0 before:h-[calc(var(--gap)+1px)] before:w-full before:content-[""]',
  'after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-[""]',
  '[transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]'
].join(' ');

const variantIcon: Record<ToastVariant, ComponentType<{ size?: number; weight?: 'fill'; className?: string }>> = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  info: InfoIcon,
  warning: WarningIcon
};

function ToastList() {
  const { toasts } = Toast.useToastManager();
  return toasts.map((item) => {
    const variant = (item.type as ToastVariant | undefined) ?? 'info';
    const Icon = variantIcon[variant];
    return (
      <Toast.Root
        key={item.id}
        toast={item}
        swipeDirection="up"
        role="group"
        className={twMerge(
          'box-border flex max-h-[800px] items-start overflow-visible rounded-lg p-3 font-raleway text-sm shadow-sm outline-none select-none',
          stackClassName,
          variantClassName[variant]
        )}
      >
        <Toast.Content className="flex min-w-0 flex-1 items-start gap-2.5 overflow-hidden transition-opacity duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] data-[behind]:opacity-0 data-[expanded]:opacity-100">
          <Icon size={20} weight="fill" className="shrink-0" />
          <Toast.Title
            render={<p />}
            role={variant === 'error' ? 'alert' : 'status'}
            className="m-0 min-w-0 flex-1 text-left text-sm font-medium leading-5"
          />
          <Toast.Close
            aria-label="Close notification"
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center border-0 bg-transparent p-0 text-current opacity-70 outline-none transition-opacity hover:opacity-100 focus:opacity-100"
          >
            <XIcon size={14} weight="bold" />
          </Toast.Close>
        </Toast.Content>
      </Toast.Root>
    );
  });
}

export function Toaster({ children }: { children: ReactNode }) {
  return (
    <Toast.Provider toastManager={toastManager} timeout={5000} limit={4}>
      {children}
      <Toast.Portal>
        <Toast.Viewport
          className="fixed top-4 right-4 z-toast m-0 box-border w-[320px] overflow-visible p-0 outline-none max-[480px]:inset-x-0 max-[480px]:top-0 max-[480px]:w-screen"
          aria-label="Notifications"
        >
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  );
}
